import React, { useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { getUserProfile, UserProfile } from '../../services/userProfileService';
import { JIGSAW_PUZZLES } from '../../data/puzzles';

const Collection: React.FC = () => {
    const history = useHistory();
    const [user, setUser] = useState<UserProfile | null>(null);

    useEffect(() => {
        const profile = getUserProfile();
        setUser(profile);
    }, []);

    if (!user) {
        return <div style={{ textAlign: 'center', padding: '40px 20px' }}>Loading collection...</div>;
    }

    const completedIds = new Set<string>(user.stats.completedPuzzleIds);

    const completionPercent = Math.round((completedIds.size / JIGSAW_PUZZLES.length) * 100);

    return (
        <div style={{
            padding: '20px',
            maxWidth: '600px',
            margin: '0 auto',
            paddingBottom: '100px',
        }}>
            <button
                onClick={() => history.goBack()}
                style={{
                    marginBottom: '20px',
                    padding: '10px 16px',
                    background: theme.colors.bgSecondary,
                    border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: theme.colors.textPrimary,
                    fontWeight: 600,
                }}
            >
                ← Back
            </button>

            <h1 style={{
                fontSize: '28px',
                fontWeight: 700,
                color: theme.colors.textPrimary,
                marginBottom: '8px',
            }}>
                🎨 My Collection
            </h1>

            <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginBottom: '24px',
            }}>
                {completedIds.size} of {JIGSAW_PUZZLES.length} puzzles completed
            </p>

            {/* Progress Bar */}
            <div style={{
                marginBottom: '32px',
                padding: '20px',
                backgroundColor: theme.colors.cardBg,
                borderRadius: '12px',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                }}>
                    <span style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                        Collection Progress
                    </span>
                    <span style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: theme.colors.accent,
                    }}>
                        {completionPercent}%
                    </span>
                </div>

                <div style={{
                    width: '100%',
                    height: '16px',
                    backgroundColor: theme.colors.cardBorder,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        width: `${completionPercent}%`,
                        height: '100%',
                        backgroundColor: theme.colors.accent,
                        borderRadius: '8px',
                        transition: 'width 0.3s ease',
                    }} />
                </div>
            </div>

            {/* Puzzles Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
            }}>
                {JIGSAW_PUZZLES.map(puzzle => {
                    const isCompleted = completedIds.has(puzzle.id);

                    return (
                        <Link
                            key={puzzle.id}
                            to={`/play/${puzzle.id}`}
                            style={{
                                position: 'relative',
                                paddingBottom: '100%',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: theme.shadows.sm,
                                textDecoration: 'none',
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundImage: `url(${puzzle.url})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                opacity: isCompleted ? 1 : 0.6,
                                transition: 'opacity 0.2s ease',
                            }} />

                            {/* Completed Badge */}
                            {isCompleted && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(0,0,0,0.3)',
                                }}>
                                    <div style={{
                                        fontSize: '32px',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                    }}>
                                        ✅
                                    </div>
                                </div>
                            )}

                            {/* Title Overlay */}
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                                padding: '12px 8px 8px',
                                color: '#fff',
                            }}>
                                <div style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {puzzle.title}
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default Collection;
